/**
 * ใครยังมีสิทธิ์รับเงินของ "งวด" ไหน — กติกาเดียวที่ใช้ร่วมกันทั้งหน้าจอและหลังบ้าน.
 *
 * ⭐⭐ เคสจริง Mangmee 2026-08-04: พนักงานลาออกวันที่ 30 แต่ค่าคอมตัดรอบวันที่ 25 →
 * พอตั้งสถานะเป็น "ลาออก" ชื่อเขาหายจากทุกรายการทันที ค่าคอมงวดสุดท้ายเลยตกหล่น
 * ต้องมาโอนย้อนหลัง. แต่ถ้าดึงชื่อกลับมาถาวรก็จะเผลอโอนให้คนที่ออกไปแล้วในเดือนถัดไป.
 *
 * ทางออก: **เลิกตัดสินด้วย "สถานะตอนนี้" → ตัดสินด้วย "งวดที่รอบนี้จ่ายให้ เทียบกับ
 * วันสิ้นสุด"** — ออก 30 ก.ค. ยังอยู่ในงวด ก.ค. และก่อนหน้า · งวด ส.ค. ขึ้นไปหายเอง
 * โดยไม่ต้องพึ่งความจำใคร.
 *
 * ⚠️ ต้องคู่กับกฎ "สถานะลาออก/เลิกจ้าง ต้องกรอกวันสิ้นสุด" (hr.ts upsertEmployeeHandler)
 * ไม่งั้น end_date ว่าง = ตัดสินไม่ได้ → คืน false ให้ไปโผล่ในกล่องยืนยันแทนการเดาแทนคน
 */

export interface LeaverEligibilityEmployee {
  status?: string | null;
  end_date?: string | null;
}

export interface LeaverEligibilityRound {
  period?: string | null;
  pay_date?: string | null;
}

/** งวดของรอบจ่าย — รอบเก่าที่ยังไม่ระบุงวด ใช้เดือนของวันโอนแทน (เกณฑ์เดียวกับ guessPeriod) */
export function roundPeriodOf(round: LeaverEligibilityRound | null | undefined): string {
  return round?.period || (round?.pay_date ?? '').slice(0, 7);
}

/** ออกจากงานแล้วหรือยัง + วันสิ้นสุด (null = ยังไม่กรอก = ตัดสินเองไม่ได้) */
export function leaverOf(e: LeaverEligibilityEmployee): { left: boolean; endDate: string | null } {
  const left = e.status === 'resigned' || e.status === 'terminated';
  return { left, endDate: left ? (e.end_date || null) : null };
}

/**
 * มีสิทธิ์อยู่ในรอบของงวดนี้ไหม
 * - ยังทำงานอยู่ (รวม probation/pending_start) → ได้เสมอ
 * - ออกแล้ว + มีวันสิ้นสุด → ได้เมื่อ "เดือนที่ออก >= งวดของรอบ"
 * - ออกแล้ว + ไม่มีวันสิ้นสุด → ไม่ได้ (ให้คนยืนยันเอง ห้ามเดาแทน)
 * - งวดว่าง (รอบไม่มีทั้ง period และ pay_date) → ไม่กันใครออก ปล่อยตามสถานะเดิม
 */
export function eligibleForPeriod(e: LeaverEligibilityEmployee, period: string): boolean {
  const { left, endDate } = leaverOf(e);
  if (!left) return true;
  if (!period) return true;
  if (!endDate) return false;
  return endDate.slice(0, 7) >= period;
}
